import { useEffect, useRef, useState, type CSSProperties } from "react";
import { PeriodSelector } from "@/components/billing/BillingPage";
import { ContextMenu, type ContextMenuItem } from "@/components/common/ContextMenu";
import { FilterButton } from "@/components/common/FilterButton";
import {
	downloadFinanceCreditsCsv,
	downloadFinanceDeviceLicenseEventsCsv,
	getFinanceCreditLedger,
	getFinanceDeviceLicenseEvents,
	getFinanceOrganizationCredits,
	type FinanceCreditLedger,
	type FinanceDeviceLicenseEvent,
	type FinanceLedgerInterval,
	type FinanceOrganizationCredits,
} from "@/lib/api";
import {
	billingCsvFilename,
	billingPeriodStart,
	DEVICE_LICENSE_EVENT_LABELS,
	deviceLicenseEventActor,
	formatCredits,
	type BillingPeriod,
} from "@/lib/billing";
import { downloadBlob } from "@/lib/download-file";
import { formatDateTime } from "@/lib/format-datetime";
import { LEDGER_INTERVALS } from "@/lib/finance";
import { sum } from "@/lib/sum";
import { useToastStore } from "@/stores/useToastStore";

/**
 * 管理ページ (/admin) の「クレジット」タブ (SuperAdmin のみ)。上に全組織の残高と期間内の
 * 付与・消費・失効を並べ、選んだ組織の台帳 (日次または月次。カテゴリ別の消費と末日の残高) と
 * 期間内のデバイスのライセンスの変化を下に出す。
 */
export function CreditsDashboardPage() {
	const [period, setPeriod] = useState<BillingPeriod>(INITIAL_PERIOD);
	const [range, setRange] = useState(() => periodRange(INITIAL_PERIOD, new Date()));
	const [interval, setLedgerInterval] = useState<FinanceLedgerInterval>("daily");
	// 期間を変えて取り直す間も前の一覧を残し、表が消えてちらつかないようにする。
	const [loaded, setLoaded] = useState<Loaded<FinanceOrganizationCredits[]> | null>(null);
	const [failed, setFailed] = useState<Failed | null>(null);
	const [selectedId, setSelectedId] = useState<string | null>(null);
	const key = rangeKey(range);

	useEffect(() => {
		let cancelled = false;
		getFinanceOrganizationCredits(range)
			.then((data) => {
				if (cancelled) return;
				setLoaded({ key, data });
				setFailed(null);
			})
			.catch((err: Error) => {
				console.error("組織別のクレジットの取得に失敗しました。", err);
				if (!cancelled) setFailed({ key, message: err.message });
			});
		return () => {
			cancelled = true;
		};
	}, [range, key]);

	const selectPeriod = (next: BillingPeriod) => {
		setPeriod(next);
		setRange(periodRange(next, new Date()));
	};

	const organizations = loaded?.data ?? null;
	const stale = loaded !== null && loaded.key !== key;
	const error = failed?.key === key ? failed.message : null;
	const selected = organizations?.find((o) => o.organizationId === selectedId) ?? null;

	return (
		<div
			data-testid="credits-dashboard-page"
			style={{
				padding: 32,
				height: "100%",
				overflowY: "auto",
				// 日次台帳の表示・読み込みでスクロールバーが出入りすると幅が変わり、表の列が詰め直されるため。
				scrollbarGutter: "stable",
				background: "var(--surface-1)",
			}}
		>
			<h2 style={titleStyle}>クレジット</h2>
			<p style={descriptionStyle}>{DESCRIPTION}</p>
			<div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
				<PeriodSelector period={period} onSelect={selectPeriod} />
				<IntervalSelector interval={interval} onSelect={setLedgerInterval} />
				<div style={{ marginLeft: "auto" }}>
					<CsvExportMenu
						organizations={organizations}
						selected={selected}
						range={range}
						interval={interval}
					/>
				</div>
			</div>

			{error !== null && <p style={errorStyle}>{error}</p>}
			{error === null && organizations === null && <p style={loadingStyle}>読み込み中…</p>}
			{error === null && organizations !== null && (
				<div style={staleStyle(stale)}>
					<OrganizationTable
						organizations={organizations}
						selectedId={selectedId}
						onSelect={setSelectedId}
					/>
				</div>
			)}

			{selected !== null && (
				<>
					<OrganizationLedger organization={selected} range={range} interval={interval} />
					<OrganizationLicenseEvents organization={selected} range={range} />
				</>
			)}
		</div>
	);
}

/** CSV を取得して保存する。organizationId を省略すると全組織。 */
type CsvExport = (organizationId?: string) => Promise<void>;

/**
 * 表示中の期間のクレジットの台帳かライセンスの変化を CSV に書き出すメニュー。
 * それぞれ全組織か、一覧で選んだ組織だけかを選ぶ。
 */
function CsvExportMenu({
	organizations,
	selected,
	range,
	interval,
}: {
	organizations: FinanceOrganizationCredits[] | null;
	selected: FinanceOrganizationCredits | null;
	range: PeriodRange;
	interval: FinanceLedgerInterval;
}) {
	const [menuOpen, setMenuOpen] = useState(false);
	const [exporting, setExporting] = useState(false);
	const anchorRef = useRef<HTMLButtonElement>(null);

	// 台帳は表示中の間隔で、ライセンスの変化は間隔によらず 1 変化 1 行で書き出す。
	const exportCredits: CsvExport = async (organizationId) => {
		const csv = await downloadFinanceCreditsCsv({ ...range, interval, organizationId });
		downloadBlob(csv, billingCsvFilename(new Date(), `credits-${interval}`));
	};
	const exportLicenseEvents: CsvExport = async (organizationId) => {
		const csv = await downloadFinanceDeviceLicenseEventsCsv({ ...range, organizationId });
		downloadBlob(csv, billingCsvFilename(new Date(), "device-license-events"));
	};

	const startExport = (run: CsvExport, organizationId?: string) => {
		setExporting(true);
		run(organizationId)
			.catch((err: Error) => {
				useToastStore.getState().show({ type: "error", message: err.message });
				console.error("CSVエクスポートに失敗しました。", err);
			})
			.finally(() => setExporting(false));
	};

	const exportItems = (run: CsvExport, testIdPrefix: string): ContextMenuItem[] => [
		{
			key: `${testIdPrefix}-all`,
			label: "全組織",
			testId: `${testIdPrefix}-all`,
			onSelect: () => startExport(run),
		},
		{
			key: `${testIdPrefix}-selected`,
			label: "選択した組織のみ",
			disabled: selected === null,
			title: selected === null ? "一覧で組織を選ぶと書き出せます" : undefined,
			testId: `${testIdPrefix}-selected`,
			onSelect: () => {
				if (selected !== null) startExport(run, selected.organizationId);
			},
		},
	];

	return (
		<>
			<FilterButton
				open={menuOpen}
				ref={anchorRef}
				icon="arrowD"
				label={exporting ? "書き出し中…" : "CSVエクスポート"}
				disabled={organizations === null || exporting}
				testId="credits-export-csv"
				onClick={() => setMenuOpen((v) => !v)}
			/>
			{menuOpen && anchorRef.current && organizations !== null && (
				<ContextMenu
					position={{ anchor: anchorRef.current }}
					onClose={() => setMenuOpen(false)}
					testId="credits-export-menu"
					minWidth={180}
					items={[
						{ label: "クレジットの台帳", items: exportItems(exportCredits, "credits-export") },
						{
							label: "ライセンスの変化",
							items: exportItems(exportLicenseEvents, "credits-export-license-events"),
						},
					]}
				/>
			)}
		</>
	);
}

function OrganizationTable({
	organizations,
	selectedId,
	onSelect,
}: {
	organizations: FinanceOrganizationCredits[];
	selectedId: string | null;
	onSelect: (organizationId: string) => void;
}) {
	return (
		<div style={tableFrameStyle}>
			<table className="text-caption" style={tableStyle}>
				<thead>
					<tr style={{ background: "var(--surface-2)" }}>
						<th style={{ ...headerCellStyle, textAlign: "left" }}>組織</th>
						{["残高", "期間の付与", "期間の消費", "期間の失効"].map((label) => (
							<th key={label} style={{ ...headerCellStyle, textAlign: "right" }}>
								{label}
							</th>
						))}
					</tr>
				</thead>
				<tbody className="select-text">
					{organizations.map((o) => (
						<tr
							key={o.organizationId}
							data-testid={`credits-organization-${o.organizationId}`}
							className="cursor-pointer hover:bg-row-hover-bg"
							style={{
								background: o.organizationId === selectedId ? "var(--row-selected-bg)" : undefined,
							}}
							onClick={() => onSelect(o.organizationId)}
						>
							<td style={{ ...cellStyle, fontWeight: 600 }}>{o.organizationName}</td>
							<CreditsCell credits={o.balanceCredits} signed />
							<CreditsCell credits={o.grantedCredits} />
							<CreditsCell credits={o.consumedCredits} />
							<CreditsCell credits={o.expiredCredits} />
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

/** 台帳の集計間隔のセレクタ。 */
function IntervalSelector({
	interval,
	onSelect,
}: {
	interval: FinanceLedgerInterval;
	onSelect: (interval: FinanceLedgerInterval) => void;
}) {
	const [menuOpen, setMenuOpen] = useState(false);
	const anchorRef = useRef<HTMLButtonElement>(null);

	return (
		<>
			<FilterButton
				open={menuOpen}
				ref={anchorRef}
				icon="menu"
				label={LEDGER_INTERVALS.find((i) => i.id === interval)?.label ?? ""}
				testId="credits-interval-selector"
				onClick={() => setMenuOpen((v) => !v)}
			/>
			{menuOpen && anchorRef.current && (
				<ContextMenu
					position={{ anchor: anchorRef.current, align: "left" }}
					onClose={() => setMenuOpen(false)}
					testId="credits-interval-menu"
					minWidth={140}
					items={LEDGER_INTERVALS.map((i) => ({
						key: i.id,
						label: i.label,
						selectable: true,
						selected: i.id === interval,
						testId: `credits-interval-item-${i.id}`,
						onSelect: () => onSelect(i.id),
					}))}
				/>
			)}
		</>
	);
}

function OrganizationLedger({
	organization,
	range,
	interval,
}: {
	organization: FinanceOrganizationCredits;
	range: PeriodRange;
	interval: FinanceLedgerInterval;
}) {
	// 組織・期間を変えて取り直す間も前の台帳を残し、表が消えてちらつかないようにする。
	// 取り直し中に残す前の台帳は、見出しも取ったときの間隔で出す。
	const [loaded, setLoaded] = useState<Loaded<{
		ledger: FinanceCreditLedger;
		interval: FinanceLedgerInterval;
	}> | null>(null);
	const [failed, setFailed] = useState<Failed | null>(null);
	const key = `${organization.organizationId}:${rangeKey(range)}:${interval}`;

	useEffect(() => {
		let cancelled = false;
		getFinanceCreditLedger(organization.organizationId, { ...range, interval })
			.then((ledger) => {
				if (cancelled) return;
				setLoaded({ key, data: { ledger, interval } });
				setFailed(null);
			})
			.catch((err: Error) => {
				console.error("クレジットの台帳の取得に失敗しました。", err);
				if (!cancelled) setFailed({ key, message: err.message });
			});
		return () => {
			cancelled = true;
		};
	}, [organization.organizationId, range, interval, key]);

	const ledger = loaded?.data.ledger ?? null;
	const shownInterval = loaded?.data.interval ?? interval;
	const stale = loaded !== null && loaded.key !== key;
	const error = failed?.key === key ? failed.message : null;
	const newestFirst = ledger === null ? [] : [...ledger.rows].reverse();
	const columnWidths =
		ledger === null
			? []
			: [
					LEDGER_DATE_COLUMN_WIDTH,
					...LEDGER_FIXED_COLUMNS.map(() => LEDGER_AMOUNT_COLUMN_WIDTH),
					...ledger.categories.map((c) =>
						Math.max(LEDGER_AMOUNT_COLUMN_WIDTH, estimateLabelWidth(c.label) + 2 * CELL_PADDING_X),
					),
				];
	const intervalLabel = LEDGER_INTERVALS.find((i) => i.id === shownInterval)?.label ?? "";

	return (
		<section data-testid="credits-ledger" style={{ marginTop: 32 }}>
			<h3 style={sectionTitleStyle}>
				{organization.organizationName} の{intervalLabel}
			</h3>
			{error !== null && <p style={errorStyle}>{error}</p>}
			{error === null && ledger === null && <p style={loadingStyle}>読み込み中…</p>}
			{error === null && ledger !== null && ledger.rows.length === 0 && (
				<p style={{ ...loadingStyle, ...staleStyle(stale) }}>
					期間内の付与・消費・失効はありません。
				</p>
			)}
			{error === null && ledger !== null && ledger.rows.length > 0 && (
				<div style={{ ...tableFrameStyle, ...staleStyle(stale), overflowX: "auto" }}>
					{/*
					 * 列幅は中身の桁数ではなく見出しから決めて固定し、組織を切り替えても列が動かないようにする。
					 * 画面幅が足りる限りは幅いっぱいに広げ、足りないときだけ横スクロールにする。
					 */}
					<table
						className="text-caption"
						style={{ ...tableStyle, tableLayout: "fixed", minWidth: sum(columnWidths) }}
					>
						<colgroup>
							{columnWidths.map((width, i) => (
								<col key={i} style={{ width }} />
							))}
						</colgroup>
						<thead>
							<tr style={{ background: "var(--surface-2)" }}>
								<th style={{ ...headerCellStyle, textAlign: "left" }}>
									{shownInterval === "monthly" ? "月" : "日付"}
								</th>
								{LEDGER_FIXED_COLUMNS.map((label) => (
									<th key={label} style={{ ...headerCellStyle, textAlign: "right" }}>
										{label}
									</th>
								))}
								{ledger.categories.map((c) => (
									<th
										key={c.id}
										title={c.label}
										data-tooltip-if-truncated
										style={{
											...headerCellStyle,
											textAlign: "right",
											overflow: "hidden",
											textOverflow: "ellipsis",
											color: "var(--fg-4)",
										}}
									>
										{c.label}
									</th>
								))}
							</tr>
						</thead>
						<tbody className="select-text">
							{newestFirst.map((row) => (
								<tr key={row.date} data-testid={`credits-ledger-row-${row.date}`}>
									<td style={{ ...cellStyle, whiteSpace: "nowrap" }}>{row.date}</td>
									<CreditsCell credits={row.grantedCredits} />
									<CreditsCell credits={row.expiredCredits} />
									<CreditsCell credits={row.consumedCredits} />
									<CreditsCell credits={row.balanceCredits} signed />
									{ledger.categories.map((c) => (
										<CreditsCell key={c.id} credits={row.consumedByCategory[c.id] ?? 0} muted />
									))}
								</tr>
							))}
							<tr>
								<td style={{ ...cellStyle, whiteSpace: "nowrap", color: "var(--fg-3)" }}>
									期間の開始時点
								</td>
								<td colSpan={LEDGER_FIXED_COLUMNS.length - 1} style={cellStyle} />
								<CreditsCell credits={ledger.openingBalanceCredits} signed />
								<td colSpan={ledger.categories.length} style={cellStyle} />
							</tr>
						</tbody>
					</table>
				</div>
			)}
		</section>
	);
}

/** 選んだ組織の期間内のライセンスの状態の変化 (新しい順)。台帳の日次・月次の切り替えには従わない。 */
function OrganizationLicenseEvents({
	organization,
	range,
}: {
	organization: FinanceOrganizationCredits;
	range: PeriodRange;
}) {
	// 組織・期間を変えて取り直す間も前の表を残し、表が消えてちらつかないようにする。
	// 取り直し中に残す前の表は、見出しも取ったときの組織名で出す。
	const [loaded, setLoaded] = useState<Loaded<{
		events: FinanceDeviceLicenseEvent[];
		organizationName: string;
	}> | null>(null);
	const [failed, setFailed] = useState<Failed | null>(null);
	const key = `${organization.organizationId}:${rangeKey(range)}`;

	useEffect(() => {
		let cancelled = false;
		getFinanceDeviceLicenseEvents(organization.organizationId, range)
			.then((events) => {
				if (cancelled) return;
				setLoaded({ key, data: { events, organizationName: organization.organizationName } });
				setFailed(null);
			})
			.catch((err: Error) => {
				console.error("ライセンスの変化の取得に失敗しました。", err);
				if (!cancelled) setFailed({ key, message: err.message });
			});
		return () => {
			cancelled = true;
		};
	}, [organization.organizationId, organization.organizationName, range, key]);

	const events = loaded?.data.events ?? null;
	const shownName = loaded?.data.organizationName ?? organization.organizationName;
	const stale = loaded !== null && loaded.key !== key;
	const error = failed?.key === key ? failed.message : null;

	return (
		<section data-testid="credits-license-events" style={{ marginTop: 32 }}>
			<h3 style={sectionTitleStyle}>{shownName} のライセンスの変化</h3>
			{error !== null && <p style={errorStyle}>{error}</p>}
			{error === null && events === null && <p style={loadingStyle}>読み込み中…</p>}
			{error === null && events !== null && events.length === 0 && (
				<p style={{ ...loadingStyle, ...staleStyle(stale) }}>
					期間内のライセンスの変化はありません。
				</p>
			)}
			{error === null && events !== null && events.length > 0 && (
				<div style={{ ...tableFrameStyle, ...staleStyle(stale) }}>
					<table className="text-caption" style={tableStyle}>
						<thead>
							<tr style={{ background: "var(--surface-2)" }}>
								{["日時", "ステータス", "ライセンス", "デバイス", "操作者", "理由"].map((label) => (
									<th key={label} style={{ ...headerCellStyle, textAlign: "left" }}>
										{label}
									</th>
								))}
							</tr>
						</thead>
						<tbody className="select-text">
							{events.map((e, index) => (
								<tr
									// 取り直すたびに丸ごと置き換える追記のみの一覧なので、位置で識別してよい。
									key={index}
									data-testid="credits-license-event-row"
								>
									<td
										style={{
											...cellStyle,
											whiteSpace: "nowrap",
											fontVariantNumeric: "tabular-nums",
										}}
									>
										{formatDateTime(e.occurredAt)}
									</td>
									<td style={{ ...cellStyle, whiteSpace: "nowrap" }}>
										{DEVICE_LICENSE_EVENT_LABELS[e.kind]}
									</td>
									<td style={{ ...cellStyle, whiteSpace: "nowrap", color: "var(--fg-2)" }}>
										{e.licenseId}
									</td>
									<td
										style={{
											...cellStyle,
											color: e.deviceName === null ? "var(--fg-3)" : undefined,
										}}
									>
										{e.deviceName ?? "—"}
									</td>
									<td
										style={{
											...cellStyle,
											whiteSpace: "nowrap",
											color: e.actorName === null ? "var(--fg-3)" : undefined,
										}}
									>
										{deviceLicenseEventActor(e)}
									</td>
									<td style={{ ...cellStyle, overflowWrap: "anywhere" }}>
										{e.reason && (
											<>
												{e.reason}
												{e.invoiceNumber !== null && (
													<span style={{ color: "var(--fg-3)" }}> ({e.invoiceNumber})</span>
												)}
											</>
										)}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
		</section>
	);
}

/** クレジット額のセル。0 は薄い「—」、signed なら負の値を危険色で出す。 */
function CreditsCell({
	credits,
	signed,
	muted,
}: {
	credits: number;
	signed?: boolean;
	muted?: boolean;
}) {
	const color =
		signed && credits < 0
			? "var(--danger)"
			: credits === 0 || muted
				? "var(--fg-3)"
				: numericCellStyle.color;
	return (
		<td style={{ ...numericCellStyle, color }}>
			{credits === 0 && !signed ? "—" : formatCredits(credits)}
		</td>
	);
}

const INITIAL_PERIOD: BillingPeriod = "pastMonth";

/** 集計の期間 [from, to)。from が無ければ全期間。 */
interface PeriodRange {
	from?: string;
	to: string;
}

/**
 * 期間プリセットを [from, to) に固定する。一覧・台帳・CSV が別々の「今」を基準にすると、
 * 期間内の合計が互いにずれるため、期間を選んだときに 1 回だけ求めて共有する。
 */
function periodRange(period: BillingPeriod, now: Date): PeriodRange {
	return { from: billingPeriodStart(period, now)?.toISOString(), to: now.toISOString() };
}

function rangeKey(range: PeriodRange): string {
	return `${range.from ?? ""}:${range.to}`;
}

/** 取得した結果と、それを取ったときの条件 (key)。条件が変わっても取り直すまでは前の結果を出す。 */
interface Loaded<T> {
	key: string;
	data: T;
}

interface Failed {
	key: string;
	message: string;
}

/** 取り直し中の前の結果は薄く出し、読み込み中であることを示す。 */
function staleStyle(stale: boolean): CSSProperties {
	return { opacity: stale ? 0.5 : 1, transition: "opacity 150ms" };
}

/** 台帳の日付 (月) の後、カテゴリ別の列の前に並ぶ列。 */
const LEDGER_FIXED_COLUMNS = ["付与", "失効", "消費", "残高"] as const;

const CELL_PADDING_X = 8;

/** 「期間の開始時点」が収まる幅。 */
const LEDGER_DATE_COLUMN_WIDTH = 100;

/** 「-12,345,678」まで収まる幅。 */
const LEDGER_AMOUNT_COLUMN_WIDTH = 80;

/**
 * caption (12px) の見出しが 1 行に収まる幅の見積もり。全角は 12px、半角は 7px で数える
 * (実測で各見出しを数 px 上回る)。足りずに切れたときは省略記号とツールチップで全文を出す。
 */
function estimateLabelWidth(label: string): number {
	return sum([...label], (ch) => (ch.charCodeAt(0) > 0xff ? 12 : 7));
}

const DESCRIPTION =
	"組織ごとのクレジットの付与・消費・失効です (1 クレジット = 1 円)。" +
	"残高は期間によらず現在の値で、付与 − 消費 − 失効です。消費は使用した日 (JST) に計上します。";

const titleStyle: CSSProperties = {
	margin: 0,
	fontSize: "var(--text-title)",
	fontWeight: 600,
	letterSpacing: "var(--tracking-snug)",
	color: "var(--fg-1)",
};

const sectionTitleStyle: CSSProperties = {
	margin: "0 0 8px",
	fontSize: "var(--text-caption)",
	fontWeight: 600,
	letterSpacing: "var(--tracking-heading)",
	color: "var(--fg-2)",
};

const descriptionStyle: CSSProperties = {
	margin: "6px 0 16px",
	fontSize: "var(--text-caption)",
	lineHeight: "var(--leading-normal)",
	color: "var(--fg-3)",
};

const errorStyle: CSSProperties = { margin: 0, fontSize: "var(--text-ui)", color: "var(--danger)" };

const loadingStyle: CSSProperties = {
	margin: 0,
	fontSize: "var(--text-caption)",
	color: "var(--fg-3)",
};

const tableFrameStyle: CSSProperties = {
	boxShadow: "var(--shadow-ring)",
	borderRadius: "var(--radius-4)",
	overflow: "hidden",
};

const tableStyle: CSSProperties = {
	width: "100%",
	borderCollapse: "collapse",
	fontSize: "var(--text-caption)",
};

const headerCellStyle: CSSProperties = {
	padding: `8px ${CELL_PADDING_X}px`,
	color: "var(--fg-3)",
	fontWeight: 600,
	whiteSpace: "nowrap",
	verticalAlign: "bottom",
	borderBottom: "var(--pane-header-border)",
};

const cellStyle: CSSProperties = {
	padding: `6px ${CELL_PADDING_X}px`,
	color: "var(--fg-1)",
	borderTop: "1px solid var(--line-1)",
};

const numericCellStyle: CSSProperties = {
	...cellStyle,
	textAlign: "right",
	whiteSpace: "nowrap",
	fontVariantNumeric: "tabular-nums",
};
